import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Box, Typography } from "@mui/material";

// TASK-727: paired FE branch for preview QA — bot verify fix is BE auth.ts
// (signup/login assertTurnstileValid). No FE behavior change required.

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let scriptLoadPromise = null;

function loadTurnstileScript() {
  if (typeof window !== "undefined" && window.turnstile) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Turnstile script failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile script failed"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onVerify, onExpire, onError },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onVerify, onExpire, onError });

  useEffect(() => {
    callbacksRef.current = { onVerify, onExpire, onError };
  }, [onVerify, onExpire, onError]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (
        typeof window !== "undefined" &&
        window.__AUTOSHOPS_E2E_TURNSTILE_BYPASS__
      ) {
        // Role-tab click clears token then reset(); re-issue E2E token immediately.
        callbacksRef.current.onVerify?.("E2E_BYPASS_TOKEN");
        return;
      }
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    // TASK-676: Playwright sets this flag via addInitScript — humans never have it.
    if (
      typeof window !== "undefined" &&
      window.__AUTOSHOPS_E2E_TURNSTILE_BYPASS__
    ) {
      callbacksRef.current.onVerify?.("E2E_BYPASS_TOKEN");
      return undefined;
    }

    if (!siteKey || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }

      if (widgetIdRef.current != null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: "light",
        callback: (token) => callbacksRef.current.onVerify?.(token),
        "expired-callback": () => {
          callbacksRef.current.onVerify?.(null);
          callbacksRef.current.onExpire?.();
        },
        "error-callback": () => {
          callbacksRef.current.onVerify?.(null);
          callbacksRef.current.onError?.();
        },
      });
    };

    loadTurnstileScript()
      .then(renderWidget)
      .catch(() => callbacksRef.current.onError?.());

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!siteKey) {
    return (
      <Typography variant="body2" color="error" sx={{ my: 1 }}>
        Bot protection is not configured (missing REACT_APP_TURNSTILE_SITE_KEY).
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
      <div ref={containerRef} />
    </Box>
  );
});

export default TurnstileWidget;
